import { yieldThread } from './general.helper';

/**
 * Creates an array of numbers within a specified range.
 * @function asRange
 * @template T - The type of the numbers in the array.
 * @param {Array<T>} array - An array containing the start and end of the range.
 * @param {boolean} [inclusive=true] - Whether the range is inclusive.
 * @returns {Array<T>} An array of numbers within the specified range.
 */
const asRange = <T extends number>(array: Array<T>, inclusive = true) => {
  if (array.length !== 2) throw new Error('invalid length');
  const start = array[0];
  const end =
    array[1] ??
    (() => {
      throw new Error('End value is required');
    })();

  if (typeof start !== 'number' || typeof end !== 'number')
    throw new Error('Both start and end must be numbers');
  if (end < start) throw new Error('start cannot be less than end');

  return Array.from(
    {
      length: end - start + (inclusive ? 1 : 0),
    },
    (v, k) => k + start,
  );
};

/**
 * Returns the first element of an array or a default value if the array is empty.
 * @function firstOrDefault
 * @template T - The type of the elements in the array.
 * @template TDefault - The type of the default value.
 * @param {Array<T>} array - The array to get the first element from.
 * @param {TDefault} [defaultValue] - The default value to return if the array is empty.
 * @returns {T | TDefault} The first element of the array or the default value.
 */
const firstOrDefault = <T, TDefault = undefined>(
  array: Array<T>,
  defaultValue?: TDefault,
) => {
  if (!array || array.length < 1) return defaultValue;

  return array[0];
};

/**
 * Returns the last element of an array or a default value if the array is empty.
 * @function lastOrDefault
 * @template T - The type of the elements in the array.
 * @template TDefault - The type of the default value.
 * @param {Array<T>} array - The array to get the last element from.
 * @param {TDefault} [defaultValue] - The default value to return if the array is empty.
 * @returns {T | TDefault} The last element of the array or the default value.
 */
const lastOrDefault = <T, TDefault = undefined>(
  array: Array<T>,
  defaultValue?: TDefault,
) => {
  if (!array || array.length < 1) return defaultValue;

  return array[array.length - 1];
};

/**
 * Processes an array in chunks synchronously.
 * @function asChunksSync
 * @template T - The type of the elements in the array.
 * @param {Array<T>} array - The array to process in chunks.
 * @param {(chunk: T[], increment?: number) => void | boolean} callback - The callback function to process each chunk.
 * @param {number} [chunkLength=100] - The length of each chunk.
 */
const asChunksSync = <T>(
  array: Array<T>,
  callback: (chunk: T[], increment?: number) => void | boolean,
  chunkLength: number = 100,
) => {
  const gen = function* (arr: T[], chunkLength: number) {
    for (let i = 0, j = arr.length; i < j; i += chunkLength) {
      yield arr.slice(i, i + chunkLength);
    }
  };

  let i = 0;
  for (const chunk of gen(array, chunkLength)) {
    const response = callback(chunk, i);
    if (response === false) break;

    i++;
  }
};

/**
 * Processes an array in chunks asynchronously.
 * @function asChunks
 * @template T - The type of the elements in the array.
 * @param {Array<T>} array - The array to process in chunks.
 * @param {(chunk: T[], increment?: number) => Promise<void | boolean>} callback - The callback function to process each chunk.
 * @param {number} [chunkLength=100] - The length of each chunk.
 * @param {boolean} [nonBlocking=true] - Whether to yield the thread between chunks.
 */
const asChunks = async <T>(
  array: Array<T>,
  callback: (chunk: T[], increment?: number) => Promise<void | boolean>,
  chunkLength: number = 100,
  nonBlocking = true,
) => {
  const gen = async function* (arr: T[], chunkLength: number) {
    const length = arr.length;

    for (let i = 0, j = length; i < j; i += chunkLength) {
      yield arr.slice(i, i + chunkLength);

      if (!nonBlocking) continue;
      await yieldThread();
    }
  };

  let i = 0;
  for await (const chunk of gen(array, chunkLength)) {
    const response = await callback(chunk, i);
    if (response === false) break;

    i++;
  }
};

/**
 * Maps an array in chunks synchronously.
 * @function mapChunksSync
 * @template T - The type of the elements in the array.
 * @template T1 - The type of the elements in the result array.
 * @param {Array<T>} array - The array to map in chunks.
 * @param {(chunk: T[], increment?: number) => T1} callback - The callback function to map each chunk.
 * @param {number} [chunkLength=100] - The length of each chunk.
 * @returns {Array<T1>} The mapped array.
 */
const mapChunksSync = <T, T1>(
  array: Array<T>,
  callback: (chunk: T[], increment?: number) => T1,
  chunkLength: number = 100,
) => {
  const results: T1[] = [];

  const gen = function* (arr: T[], chunkLength: number) {
    const length = arr.length;

    for (let i = 0, j = length; i < j; i += chunkLength) {
      yield arr.slice(i, i + chunkLength);
    }
  };

  let i = 0;
  for (const chunk of gen(array, chunkLength)) {
    results.push(callback(chunk, i));
    i++;
  }

  return results;
};

/**
 * Maps an array in chunks asynchronously.
 * @function mapChunks
 * @template T - The type of the elements in the array.
 * @template T1 - The type of the elements in the result array.
 * @param {Array<T>} array - The array to map in chunks.
 * @param {(chunk: T[], increment?: number) => Promise<T1>} callback - The callback function to map each chunk.
 * @param {number} [chunkLength=100] - The length of each chunk.
 * @param {boolean} [nonBlocking=true] - Whether to yield the thread between chunks.
 * @returns {Promise<Array<T1>>} The mapped array.
 */
const mapChunks = async <T, T1>(
  array: Array<T>,
  callback: (chunk: T[], increment?: number) => Promise<T1>,
  chunkLength: number = 100,
  nonBlocking = true,
) => {
  const results: T1[] = [];

  const gen = async function* (arr: T[], chunkLength: number) {
    for (let i = 0, j = arr.length; i < j; i += chunkLength) {
      yield arr.slice(i, i + chunkLength);

      if (!nonBlocking) continue;
      await yieldThread();
    }
  };

  let i = 0;
  for await (const chunk of gen(array, chunkLength)) {
    results.push(await callback(chunk, i));
    i++;
  }

  return results;
};

/**
 * Sums the elements of an array.
 * @function sum
 * @template T - The type of the elements in the array.
 * @param {Array<T>} array - The array to sum.
 * @returns {number} The sum of the elements in the array.
 */
const sum = <T extends number>(array: Array<T>): number => {
  if (!array || array.length < 1) return 0;
  return array.reduce((a, b) => (Number(a || 0) + Number(b || 0)) as T);
};

export {
  asRange,
  firstOrDefault,
  lastOrDefault,
  asChunksSync,
  asChunks,
  mapChunks,
  mapChunksSync,
  sum,
};
